import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type Pagination = {
	pageSize: number;
	pageNumber: number;
};

export interface UsePaginationReturn {
	value: Pagination;
	setPageSize: (pageSize: number) => void;
	setPageNumber: (pageNumber: number) => void;
	getPageCount: (totalItems: number | undefined) => number;
}

export function usePagination(defaultPageSize = 20): UsePaginationReturn {
	const [searchParams, setSearchParams] = useSearchParams();

	const [value, setValue] = useState<Pagination>(() => {
		const pageSize = parsePositiveInt(searchParams.get('pageSize'));
		const pageNumber = parsePositiveInt(searchParams.get('pageNumber'));

		return {
			pageSize: pageSize ?? defaultPageSize,
			pageNumber: pageNumber ?? 1,
		};
	});

	const isFirstRender = useRef(true);
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);

				if (value.pageSize === defaultPageSize) {
					next.delete('pageSize');
				} else {
					next.set('pageSize', String(value.pageSize));
				}

				if (value.pageNumber === 1) {
					next.delete('pageNumber');
				} else {
					next.set('pageNumber', String(value.pageNumber));
				}

				return next;
			},
			{ replace: true },
		);
	}, [value, setSearchParams, defaultPageSize]);

	const setPageSize = (pageSize: number) => {
		setValue((prev) => ({ ...prev, pageSize }));
	};

	const setPageNumber = (pageNumber: number) => {
		setValue((prev) => ({ ...prev, pageNumber }));
	};

	const getPageCount = (totalItems: number | undefined) => {
		if (totalItems !== undefined) {
			return Math.ceil(totalItems / value.pageSize);
		}

		return 0;
	};

	return {
		value,
		setPageSize,
		setPageNumber,
		getPageCount,
	};
}

function parsePositiveInt(raw: string | null): number | null {
	if (raw === null) return null;
	const n = Number.parseInt(raw, 10);
	return Number.isFinite(n) && n > 0 ? n : null;
}